import { useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Alert, Box, Chip, IconButton, MenuItem, Select, Stack, Typography, useTheme } from "@mui/material";
import Comments from "../../Information/Comments";
import Icon from "../../../../Icon/Icon";
import Profile from "../../../../../assets/images/profile1.png";
import { ChipInfo } from "../../../../MainComponents/ChipInfo/ChipInfo";
import {
  Evaluation,
  Scenarios,
  Involved,
  ActivityPlan,
  Resources,
  Interviews,
  Evidences,
  Conclusion,
} from "../";
import { GeneralData } from "./GeneralData";
import { CaseClassification } from "./CaseClassification";
import { ReportSummary } from "./ReportSummary";
import { InvestigationButton } from "../components";
import { patchInvestigation } from "../../../../../apis";
import { IconHeader } from "../../../../Icon/IconHader/IconHeader";
import { isInvestigationConcluded } from "../helpers/investigationStatus";

// Summary of the investigation's components
export const Folder = ({
  catalogCompanyRelationship,
  grievanceClassificationCat,
  reportDetail,
  investigation,
  setInvestigation,
  interviews = [],
  assignedUsers = [],
  handleSelectedSection,
  openModalConfirm,
  closeModalConfirm,
}) => {
  // ---> Custom Hooks <---//
  const [t] = useTranslation("investigation");
  const [t2] = useTranslation("information");
  const theme = useTheme();

  // Si la investigación ya está cerrada (llegó la fecha de conclusión), no
  // permitimos navegar a editar las secciones desde el resumen.
  const isConcluded = isInvestigationConcluded(investigation?.conclusion_at);
  const sectionEditHandler = isConcluded ? undefined : handleSelectedSection;

  // Básica: evaluación, involucrados, evidencias y conclusión.
  // Avanzada agrega: ubicaciones, plan de actividades, recursos y entrevistas.
  const isAdvanced = investigation?.advance ?? false;

  // Usuarios asignados que tienen el rol "investigation_responsable": son los
  // candidatos a ser el responsable (lead) de esta investigación.
  const investigationResponsables = (assignedUsers ?? []).filter((user) =>
    user?.assign_details?.rolesInvestigation?.includes("investigation_responsable"),
  );

  // Valor actual del selector: el lead guardado, siempre que siga siendo un
  // candidato válido (por si le quitaron el rol después de asignarlo).
  const leadUserId = investigationResponsables.some(
    (user) => user.id === investigation?.lead_user_id,
  )
    ? investigation.lead_user_id
    : "";
  const leadUser = investigationResponsables.find(
    (user) => user.id === leadUserId,
  );

  // El selector solo aplica cuando hay más de un candidato; con uno solo, ese
  // es el responsable de facto y se muestra directo.
  const [isEditingLead, setIsEditingLead] = useState(false);

  // Guarda el responsable elegido para ESTA investigación en folder.lead_user_id.
  const handleSelectLead = async (value) => {
    const leadId = value === "" ? null : value;
    try {
      await patchInvestigation(investigation.alias, {
        folder: { lead_user_id: leadId },
      });
      setInvestigation((prev) => ({ ...prev, lead_user_id: leadId }));
    } catch (error) {
      console.error(error);
    }
  };

  // Resumen de la denuncia: su propia seccion del folder, igual que la
  // clasificacion. Se guarda como objeto para que el cifrado lo alcance.
  const handleSaveReportSummary = async (summary) => {
    await patchInvestigation(investigation.alias, {
      folder: { report_summary: summary },
    });
    setInvestigation((prev) => ({ ...prev, report_summary: summary }));
  };

  // Clasificacion del caso: se guarda como su propia seccion del folder.
  const handleSaveCaseClassification = async (classification) => {
    await patchInvestigation(investigation.alias, {
      folder: { case_classification: classification },
    });
    setInvestigation((prev) => ({
      ...prev,
      case_classification: classification,
    }));
  };

  const handleSaveFolder = async (patch) => {
    const { folder, ...localUpdate } = patch;

    try {
      await patchInvestigation(investigation.alias, patch);
      setInvestigation((prev) => ({
        ...prev,
        ...localUpdate,
        dates: { ...(prev.dates ?? {}), ...folder.dates },
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const openModalReopenInvestigation = () => {
    openModalConfirm({
      title: t("General.reopen_investigation"),
      titleColor: theme.palette.green.main,
      variant: "success",
      ethiquin: "investigation",
      description1: (
        <>
          <Typography mt={2}>
            {t("General.reopen_investigation_subtitle")}
          </Typography>
          <Typography mt={2}>
            {t("General.reopen_investigation_subtitle_2")}
          </Typography>
          <Typography mt={2}>
            <span style={{ color: theme.palette.red.main, fontWeight: 600 }}>
              {t("General.important")}:
            </span>{" "}
            &nbsp;
            {t("General.important_description")}
          </Typography>
        </>
      ),
      question: t("General.reopen_investigation_confirm"),
      onClickRight: handleReopenInvestigation,
      leftButtonText: t("General.cancel"),
      rightButtonText: t("General.confirm"),
    });
  };

  const handleReopenInvestigation = async () => {
    const conclusion = {
      conclusion: investigation.conclusion ?? "",
      comment: investigation.comment ?? "",
      conclusion_at: null,
      folder: {
        dates: {
          ...(investigation.dates ?? {}),
          reopen_at: dayjs().toDate(),
        },
      },
    };
    try {
      await handleSaveFolder(conclusion);
      closeModalConfirm();
    } catch (error) {
      console.error(error);
    }
  };

  const severityOpts = {
    "type::None": t("General.none"),
    "type::null": t("General.none"),
    "type::low": t("General.low"),
    "type::medium": t("General.medium"),
    "type::hight": t("General.high"),
    "type::other": t("General.other"),
  };

  const priorityOpts = {
    low: t("General.low"),
    medium: t("General.medium"),
    hight: t("General.high"),
  };

  return (
    <Box className="dyTheme1" padding={2}>
      <Stack direction="row" justifyContent="space-between">
        {/* Titulo del reporte */}
        <Box>
          <img src={Profile} height={"24px"} style={{ marginRight: 10 }} />
          <Typography component="span">
            {t("General.report_investigation")} &nbsp;
          </Typography>
          <Typography component="span" fontWeight={600}>
            {reportDetail?.folio}
          </Typography>

          {investigationResponsables.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" mt={1}>
              <Icon
                name="bold_user_tick"
                size={18}
                color={theme.palette.green.main}
              />
              <Typography component="span" color="secondary.main" mr={0.5}>
                {t("investigation.investigation_lead")}:
              </Typography>

              {investigationResponsables.length === 1 ? (
                // Un único candidato: es el responsable de facto, se muestra directo.
                <Typography
                  component="span"
                  fontWeight={600}
                  color={theme.palette.green.main}
                >
                  {investigationResponsables[0].name}
                </Typography>
              ) : isEditingLead && !isConcluded ? (
                // Modo edición: selector para elegir/cambiar el responsable.
                <>
                  <Select
                    size="small"
                    displayEmpty
                    autoFocus
                    value={leadUserId}
                    onChange={(e) => {
                      handleSelectLead(e.target.value);
                      setIsEditingLead(false);
                    }}
                    sx={{ height: 28, fontSize: 13, minWidth: 200 }}
                  >
                    <MenuItem value="">
                      <em>{t("investigation.undefined")}</em>
                    </MenuItem>
                    {investigationResponsables.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <IconButton size="small" onClick={() => setIsEditingLead(false)}>
                    <Icon name="bold_close" size={16} />
                  </IconButton>
                </>
              ) : (
                // Modo lectura: nombre del responsable o "Sin definir".
                <>
                  {leadUser ? (
                    <Typography
                      component="span"
                      fontWeight={600}
                      color={theme.palette.green.main}
                    >
                      {leadUser.name}
                    </Typography>
                  ) : (
                    <Typography
                      component="span"
                      fontStyle="italic"
                      color="secondary.main"
                    >
                      {t("investigation.undefined")}
                    </Typography>
                  )}
                  {!isConcluded && (
                    <IconButton
                      size="small"
                      onClick={() => setIsEditingLead(true)}
                    >
                      <Icon
                        name="outline_edit_2"
                        size={16}
                        color={theme.palette.primary.main}
                      />
                    </IconButton>
                  )}
                </>
              )}
            </Stack>
          )}

          {/* Con varios candidatos y sin lead elegido, el "Sin definir" pasa
              desapercibido: el warning pide explícitamente definirlo. En una
              investigación concluida ya no se puede editar, así que ahí el
              mensaje pide reabrirla para definirlo. */}
          {investigationResponsables.length > 1 && !leadUser && (
            <Alert
              severity="warning"
              sx={{ mt: 1, py: 0, fontSize: 13, alignItems: "center" }}
            >
              {isConcluded
                ? t("investigation.investigation_lead_warning_closed")
                : t("investigation.investigation_lead_warning", {
                    count: investigationResponsables.length,
                  })}
            </Alert>
          )}
        </Box>

        {/* Chips */}
        <Stack direction="row" spacing={0.5}>
          <ChipInfo
            label={
              <span>
                {t("General.severity")}:{" "}
                <b>
                  {severityOpts[reportDetail?.severity?.path] ??
                    severityOpts["type::None"]}
                </b>
              </span>
            }
            icon={
              <div style={{ display: "grid", placeContent: "center" }}>
                <Icon name="bold_flash_2" size={16} />
              </div>
            }
            color="yellow"
            maxWidth={200}
            showAllways={false}
          />
          {!Array.isArray(reportDetail?.locked) &&
            Object.keys(reportDetail?.locked).length > 0 && (
              <ChipInfo
                label={<b>{t("General.bloqued")}</b>}
                icon={
                  <div style={{ display: "grid", placeContent: "center" }}>
                    <Icon name="bold_lock_slash" size={16} />
                  </div>
                }
                color="red"
                maxWidth={200}
                showAllways={false}
              />
            )}
          {!Array.isArray(reportDetail?.compromised_anonymity) &&
            Object.keys(reportDetail?.compromised_anonymity).length > 0 && (
              <ChipInfo
                label={<b>{t("General.compromised_anonymity")}</b>}
                icon={
                  <div style={{ display: "grid", placeContent: "center" }}>
                    <Icon name="bold_user_remove" size={16} />
                  </div>
                }
                color="teal"
                maxWidth={200}
                showAllways={false}
              />
            )}
          {!Array.isArray(reportDetail?.hostile_place) &&
            Object.keys(reportDetail?.hostile_place).length > 0 && (
              <ChipInfo
                label={<b>{t("General.hostile_place")}</b>}
                icon={
                  <div style={{ display: "grid", placeContent: "center" }}>
                    <Icon name="bold_radar" size={16} />
                  </div>
                }
                color="purple"
                maxWidth={200}
                showAllways={false}
              />
            )}
          <Chip
            label={
              <b>
                {t("General.priority")}{" "}
                {priorityOpts[investigation?.evaluation?.priority] ??
                  t("General.without_assign")}
              </b>
            }
            color="orange"
            variant="outlined"
            sx={{ height: 24, fontSize: 12 }}
          />
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        justifyContent="space-between"
        mt={2}
      >
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {/* Inicio de la investigación */}
          <Stack
            direction="row"
            display="flex"
            flex={"1 0 285px"}
            alignItems="center"
            spacing={0.5}
          >
            <Icon
              name="calendar_bold"
              size={24}
              color={theme.palette.aquaSky.main}
            />
            <Typography component="span" color="secondary.main">
              {t("General.investigation_start")}:
            </Typography>
            <Typography
              component="span"
              fontWeight={700}
              color={theme.palette.aquaSky.main}
            >
              {dayjs(investigation?.created_at).isValid()
                ? dayjs(investigation?.created_at).format("DD/MMM/YYYY")
                : t("General.no_information")}
            </Typography>
          </Stack>
          {/* Investigación reabierta */}
          {investigation?.dates?.reopen_at && (
            <Stack
              direction="row"
              display="flex"
              flex={"1 0 285px"}
              alignItems="center"
              spacing={0.5}
            >
              <Icon
                name="calendar_bold"
                size={24}
                color={theme.palette.green.main}
              />
              <Typography component="span" color="secondary.main">
                {t("General.investigation_reopened")}:
              </Typography>
              <Typography
                component="span"
                fontWeight={700}
                color={theme.palette.green.main}
              >
                {dayjs(investigation?.dates?.reopen_at).isValid()
                  ? dayjs(investigation?.dates?.reopen_at).format("DD/MMM/YYYY")
                  : t("General.no_information")}
              </Typography>
            </Stack>
          )}

          {/* Fecha de conclusión */}
          <Stack
            direction="row"
            display="flex"
            flex={"1 0 285px"}
            alignItems="center"
            spacing={0.5}
          >
            <Icon
              name="calendar_bold"
              color={theme.palette.red.main}
              size={24}
            />
            <Typography component="span" color="secondary.main">
              {t("General.conclusion_date_label")}:
            </Typography>
            <Typography
              component="span"
              fontWeight={700}
              color={theme.palette.red.main}
            >
              {dayjs(investigation?.conclusion_at).isValid()
                ? dayjs(investigation?.conclusion_at).format("DD/MMM/YYYY")
                : t("General.no_information")}
            </Typography>
          </Stack>
        </Stack>
        <Box maxWidth={"200px"} flexShrink={1} mt={{ xs: 2, lg: 0 }}>
          {investigation.conclusion && investigation.conclusion_at ? (
            <InvestigationButton
              label={t("General.reopen_investigation_btn")}
              variant="success"
              onClick={openModalReopenInvestigation}
              wordBreak="break-word"
              fullWidth
            />
          ) : (
            <InvestigationButton
              label={t("General.conclude_investigation_btn")}
              variant="danger"
              onClick={() => handleSelectedSection("conclusion")}
              wordBreak="break-word"
              fullWidth
              disabled={investigation.conclusions?.length === 0}
            />
          )}
        </Box>
      </Stack>

      {/* Informacion del reporte */}
      <Box
        className="rounded dyTheme2 pt-[10] pe-[20] pb-[16] ps-[20]"
        mt={1.7}
        sx={{ border: `1px solid ${theme.palette.primary.main}` }}
        marginBottom={2}
      >
        <Typography
          variant="h5"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          {reportDetail?.subject || t2("information.no_subject")}
        </Typography>

        <Typography variant="h6" color="primary" className="mt-[15]">
          {`${t2("information.report_description")}:`}
        </Typography>

        <Comments
          padding={0}
          maxLength={500}
          text={reportDetail?.description || t2("information.no_description")}
          color="secondary"
        />
      </Box>

      <ReportSummary
        investigation={investigation}
        onSave={handleSaveReportSummary}
        justView={isConcluded}
      />

      <CaseClassification
        investigation={investigation}
        onSave={handleSaveCaseClassification}
        justView={isConcluded}
      />

      <GeneralData
        reportDetail={reportDetail}
        investigation={investigation}
        catalogCompanyRelationship={catalogCompanyRelationship}
        grievanceClassificationCat={grievanceClassificationCat}
      />

      <Stack spacing={2}>
        <Evaluation
          investigation={investigation}
          handleSelectedSection={sectionEditHandler}
          justView
        />

        {isAdvanced && (
          <Scenarios
            reportDetail={reportDetail}
            investigation={investigation}
            handleSelectedSection={sectionEditHandler}
            justView
          />
        )}

        <Involved
          reportDetail={reportDetail}
          investigation={investigation}
          handleSelectedSection={sectionEditHandler}
          justView
        />

        {isAdvanced && (
          <ActivityPlan
            investigation={investigation}
            handleSelectedSection={sectionEditHandler}
            justView
          />
        )}

        {isAdvanced && (
          <Resources
            reportDetail={reportDetail}
            investigation={investigation}
            handleSelectedSection={sectionEditHandler}
            justView
          />
        )}

        {isAdvanced && (
          <Interviews
            reportDetail={reportDetail}
            investigation={investigation}
            interviews={interviews}
            handleSelectedSection={sectionEditHandler}
            justView
            summaryMode
          />
        )}

        <Evidences
          investigation={investigation}
          handleSelectedSection={sectionEditHandler}
          justView
        />

        <Conclusion
          investigation={investigation}
          handleSelectedSection={sectionEditHandler}
          justView
        />
      </Stack>
    </Box>
  );
};
